/**
 * Oracle Trades · TV / Wall Mode
 * ---------------------------------------------------------------------------
 * Read-only full-screen view for a 75" TV half (portrait, ~1920 × 2160).
 *
 * Layout:
 *   Row 1 (6vh)   Header               (always)
 *   Row 2 (24vh)  Radar + Neural Map   (always · rotation + galaxy bg)
 *   Row 3 (24vh)  Today's Signals      (PERMANENT · big cards)
 *   Row 4 (40vh)  Rotating pane        (5 pages · auto 20s · manual override)
 *                   1. Open Positions
 *                   2. Risk Lab status
 *                   3. Reactor · Limits
 *                   4. Recent Closed Trades
 *                   5. Blocked Today
 *   Row 5 (6vh)   Controls             (◀ page x/5 ▶ PIN DIM EXIT)
 *
 * Rules enforced throughout:
 *   • Reuses existing /orb/* APIs. Zero logic changes.
 *   • No backdrop-filter / blur. Glow kept only on status badges + sweep.
 *   • Animations pause on tab hidden and respect prefers-reduced-motion.
 *   • Dim toggle (brightness .7, saved to localStorage).
 *   • OLED anti-burn: 2 px pixel shift every 4 min.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "@/lib/api";

const C = {
  bg:     "#02040b",
  panel:  "rgba(10,18,40,0.78)",
  stroke: "rgba(59,217,255,0.28)",
  cyan:   "#4de8ff",
  green:  "#45f0a6",
  amber:  "#ffc65c",
  red:    "#ff5c7a",
  muted:  "#8ea6c6",
  text:   "#e9fbff",
};
const RL = { green: C.green, amber: C.amber, red: C.red };

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------
const money  = (v) => (v == null || isNaN(v) ? "—" :
  `${v < 0 ? "-" : ""}$${Math.abs(Number(v)).toLocaleString("en-US", { maximumFractionDigits: 0 })}`);
const signed = (v) => (v == null || isNaN(v) ? "—" :
  `${v >= 0 ? "+" : "-"}$${Math.abs(Number(v)).toLocaleString("en-US", { maximumFractionDigits: 0 })}`);
const pct    = (v, d = 1) => (v == null || isNaN(v) ? "—" : `${Number(v).toFixed(d)}%`);
const clockUK = (iso) => iso ? new Date(iso).toLocaleTimeString("en-GB", { hour:"2-digit", minute:"2-digit", timeZone:"Europe/London" }) : "";
const clockNY = (iso) => iso ? new Date(iso).toLocaleTimeString("en-GB", { hour:"2-digit", minute:"2-digit", timeZone:"America/New_York" }) : "";

// ---------------------------------------------------------------------
// Entry component
// ---------------------------------------------------------------------
export default function OracleTradesTV() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [live, setLive] = useState(null);
  const [prices, setPrices] = useState({});
  const [err, setErr] = useState("");
  const [dim, setDim] = useState(() => localStorage.getItem("tv_dim") === "1");
  const [shift, setShift] = useState({ x: 0, y: 0 });
  const [page, setPage] = useState(0);
  const [pinned, setPinned] = useState(false);

  // --- PWA install (chromeless window) --------------------------------
  // Chrome shows a native install prompt when the site has a manifest + a
  // service worker + user interaction. Both already exist for this app
  // (/manifest.json + /sw.js). We also capture `beforeinstallprompt` so a
  // button in the TV footer can trigger install on demand. If Chrome
  // never fires the event (common: already installed origin, Firefox,
  // iOS Safari), the button opens a manual-instructions dialog.
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const [isInstalled, setIsInstalled] = useState(
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)")?.matches ||
     window.navigator.standalone === true)
  );
  useEffect(() => {
    const onBip = (e) => { e.preventDefault(); setInstallPrompt(e); };
    const onInstalled = () => { setIsInstalled(true); setInstallPrompt(null); };
    const mm = window.matchMedia?.("(display-mode: standalone)");
    const onMode = (e) => setIsInstalled(e.matches);
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    mm?.addEventListener?.("change", onMode);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
      mm?.removeEventListener?.("change", onMode);
    };
  }, []);
  const handleInstall = useCallback(async () => {
    if (installPrompt) {
      installPrompt.prompt();
      try { await installPrompt.userChoice; } catch {}
      setInstallPrompt(null);
    } else {
      // Chrome didn't offer the automatic prompt — show manual steps.
      setShowInstallHelp(true);
    }
  }, [installPrompt]);

  // Dashboard polling (60s)
  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try { const d = await apiGet("/orb/dashboard"); if (!cancel) { setData(d); setErr(""); } }
      catch (e) { if (!cancel) setErr(e.message || "Failed to load dashboard"); }
    };
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 60_000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  // Risk Lab polling (5 min)
  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try { const d = await apiGet("/orb/risk-lab"); if (!cancel) setLive(d); }
      catch (_) {}
    };
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 5 * 60_000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  // Live prices (60s during market hours) — covers BOTH open positions and
  // today's signal cards so a just-triggered setup like NET shows a LIVE
  // price even before it becomes an open trade.
  const openTrades = data?.open_trades || [];
  const signalSymbols = useMemo(
    () => (data?.todays_signals || []).map((s) => s?.symbol).filter(Boolean),
    [data?.todays_signals]
  );
  const symKey = useMemo(() => {
    const s = new Set();
    for (const t of openTrades) if (t?.symbol) s.add(String(t.symbol).toUpperCase());
    for (const sym of signalSymbols) if (sym) s.add(String(sym).toUpperCase());
    return [...s].sort().join(",");
  }, [openTrades, signalSymbols]);
  const isMarketOpen = !!data?.market?.is_open;
  useEffect(() => {
    if (!symKey) { setPrices({}); return; }
    let cancel = false, timer;
    const once = async (force = false) => {
      try {
        const url = force
          ? `/orb/live-prices?symbols=${encodeURIComponent(symKey)}&force=1&_t=${Date.now()}`
          : `/orb/live-prices?symbols=${encodeURIComponent(symKey)}`;
        const r = await apiGet(url);
        if (!cancel) setPrices((prev) => ({ ...prev, ...(r.prices || {}) }));
      } catch (_) {}
    };
    const schedule = () => {
      if (cancel) return;
      if (document.hidden) { timer = setTimeout(schedule, 15_000); return; }
      once();
      timer = setTimeout(schedule, isMarketOpen ? 60_000 : 15 * 60_000);
    };
    schedule();
    const onForce = () => once(true);
    window.addEventListener("orb:refresh-prices", onForce);
    return () => {
      cancel = true; clearTimeout(timer);
      window.removeEventListener("orb:refresh-prices", onForce);
    };
  }, [symKey, isMarketOpen]);

  // OLED anti-burn pixel shift every 4 min
  useEffect(() => {
    const positions = [[0,0],[2,0],[2,2],[0,2],[-2,2],[-2,0],[-2,-2],[0,-2]];
    let i = 0;
    const t = setInterval(() => {
      i = (i + 1) % positions.length;
      setShift({ x: positions[i][0], y: positions[i][1] });
    }, 4 * 60_000);
    return () => clearInterval(t);
  }, []);

  // Enrich open trades with live P&L
  const enriched = useMemo(() => openTrades.map((t) => {
    const sym = String(t.symbol || "").toUpperCase();
    const px = prices[sym];
    if (!px || px.price == null) return { ...t, last_price: null, stale: !!px?.stale };
    const p = Number(px.price);
    const entry = Number(t.entry) || 0, stop = Number(t.stop) || 0;
    const target = Number(t.target) || 0, shares = Number(t.shares) || 0;
    const risk = entry - stop;
    const pnlR = risk > 0 ? (p - entry) / risk : 0;
    const barPos = target === stop ? 0.5 : Math.max(-0.08, Math.min(1.08, (p - stop) / (target - stop)));
    return {
      ...t, last_price: p, last_time: px.time || null, stale: !!px.stale,
      pnl_dollars: (p - entry) * shares,
      pnl_pct: entry > 0 ? (p / entry - 1) : 0,
      pnl_r: pnlR, bar_pos: barPos,
    };
  }), [openTrades, prices]);

  const totals = useMemo(() => {
    const z = () => ({ pnl: 0, cost: 0, count: 0 });
    const a = { s5: z(), s6: z() };
    for (const t of enriched) {
      const k = t.setup === 6 ? "s6" : "s5";
      a[k].count += 1;
      a[k].cost  += Number(t.entry || 0) * Number(t.shares || 0);
      if (t.pnl_dollars != null) a[k].pnl += Number(t.pnl_dollars);
    }
    return a;
  }, [enriched]);

  const PAGES = useMemo(() => ([
    { key: "open",     label: "Open Positions"      },
    { key: "risk",     label: "Risk Lab"            },
    { key: "reactor",  label: "Reactor · Limits"    },
    { key: "closed",   label: "Recent Closed"       },
    { key: "blocked",  label: "Blocked Today"       },
  ]), []);

  // Auto-rotate every 20 s unless pinned or tab hidden
  useEffect(() => {
    if (pinned) return;
    const t = setInterval(() => {
      if (document.hidden) return;
      setPage((p) => (p + 1) % PAGES.length);
    }, 20_000);
    return () => clearInterval(t);
  }, [pinned, PAGES.length]);

  // Keyboard arrows
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") { setPage((p) => (p + 1) % PAGES.length); setPinned(true); }
      else if (e.key === "ArrowLeft") { setPage((p) => (p - 1 + PAGES.length) % PAGES.length); setPinned(true); }
      else if (e.key === " ") { e.preventDefault(); setPinned((v) => !v); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [PAGES.length]);

  const toggleDim = () => setDim((d) => {
    const n = !d; try { localStorage.setItem("tv_dim", n ? "1" : "0"); } catch {} return n;
  });

  if (err && !data) return <div style={{ color: C.red, padding: 40, background: C.bg, minHeight: "100vh" }}>Failed to load: {err}</div>;
  if (!data) return <div style={{ color: C.muted, padding: 40, background: C.bg, minHeight: "100vh" }}>Loading Oracle Trades · TV mode…</div>;

  return (
    <div data-testid="oracle-tv-root"
         className="oracle-root tv-root"
         style={{
           position: "fixed", inset: 0, background: C.bg, color: C.text,
           fontFamily: "'JetBrains Mono', 'Source Code Pro', monospace",
           overflow: "hidden",
           filter: dim ? "brightness(0.7)" : "none",
           transform: `translate(${shift.x}px, ${shift.y}px)`,
           transition: "filter .2s ease, transform 1.6s ease-in-out",
         }}>
      {/* Galaxy background — reuses the exact nebula + star layers from the
          main Oracle Trades page so the TV wall matches 1:1. */}
      <div className="nebula" />
      <div className="stars" />
      <div className="stars stars-b" />
      <div className="stars-c" />

      <div style={{
        position: "relative", zIndex: 2,
        display: "grid",
        gridTemplateColumns: "42vw 1fr",
        gridTemplateRows: "6vh 1fr 6vh",
        gap: "0.6vh 0.6vw",
        padding: "0.6vh 0.8vw",
        height: "100%",
      }}>
        {/* Row 1 — Header spans both columns */}
        <div style={{ gridColumn: "1 / span 2" }}>
          <TVHeader data={data} live={live} dim={dim} onToggleDim={toggleDim} onExit={() => nav("/oracle-trades")} />
        </div>

        {/* Row 2 Left — Radar on top, Neural Map below */}
        <div style={{
          display: "grid", gridTemplateRows: "1fr 1fr",
          gap: "0.6vh", minHeight: 0, minWidth: 0,
        }}>
          <TVRadar data={data} />
          <TVNeural data={data} />
        </div>

        {/* Row 2 Right — Today's Signals (permanent) + Rotating pane */}
        <div style={{
          display: "grid",
          gridTemplateRows: "minmax(0, 48%) minmax(0, 52%)",
          gap: "0.6vh", minHeight: 0, minWidth: 0,
        }}>
          <TVSignals data={data} prices={prices} />
          <TVCarousel pageKey={PAGES[page].key}
                      enriched={enriched} totals={totals} data={data} live={live} />
        </div>

        {/* Row 3 — Controls span both columns */}
        <div style={{ gridColumn: "1 / span 2" }}>
          <TVControls page={page} setPage={setPage}
                      pinned={pinned} setPinned={setPinned}
                      pages={PAGES} dim={dim} onToggleDim={toggleDim}
                      onExit={() => nav("/oracle-trades")}
                      canInstall={!isInstalled}
                      isInstalled={isInstalled}
                      onInstall={handleInstall} />
        </div>
      </div>
      {showInstallHelp && (
        <InstallHelpDialog onClose={() => setShowInstallHelp(false)} />
      )}
    </div>
  );
}

// =======================================================================
// Row 1 · Header
// =======================================================================
function TVHeader({ data, live }) {
  const m = data?.market || {};
  const tg = data?.telegram || {};
  const nowIso = new Date().toISOString();
  const s5 = live?.setups?.find((p) => p.setup === 5);
  const s6 = live?.setups?.find((p) => p.setup === 6);
  const openCd = m.countdown_sec != null ? fmtCountdown(m.countdown_sec) : "";
  return (
    <header data-testid="tv-header" style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: "1.5vw", padding: "0 1.2vw",
      background: C.panel, border: `1px solid ${C.stroke}`, borderRadius: "0.6vh",
      fontSize: "1.6vh",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.8vw" }}>
        <StatusDot ok={m.is_open} />
        <div style={{ lineHeight: 1.2 }}>
          <div style={{ fontWeight: 700, letterSpacing: ".15em" }}>MARKET {m.is_open ? "OPEN" : "CLOSED"}</div>
          <div style={{ color: C.muted, fontSize: "1.3vh" }}>{m.countdown_label} {openCd}</div>
        </div>
      </div>
      <div style={{ textAlign: "center", lineHeight: 1.1 }}>
        <div style={{ fontSize: "1.9vh", fontWeight: 700 }}>NY {clockNY(nowIso)} · UK {clockUK(nowIso)}</div>
        <div style={{ color: C.muted, fontSize: "1.3vh" }}>{m.ny_date || ""}</div>
      </div>
      <TelegramBadge tg={tg} />
      <RiskBadge p={s5} />
      <RiskBadge p={s6} />
    </header>
  );
}
function StatusDot({ ok }) {
  return <span style={{
    width: "1.4vh", height: "1.4vh", borderRadius: 999,
    background: ok ? C.green : C.amber,
    boxShadow: `0 0 1vh ${ok ? C.green : C.amber}`,
  }} />;
}
function TelegramBadge({ tg }) {
  const status = tg?.status || (tg?.configured ? "on" : "not_set");
  const col = status === "on" ? C.green : status === "error" ? C.red : C.muted;
  const label = status === "on" ? "ON" : status === "error" ? "ERR" : "OFF";
  return (
    <div data-testid="tv-telegram" style={{ display: "flex", alignItems: "center", gap: "0.4vw" }}>
      <span style={{ color: C.muted, fontSize: "1.3vh", letterSpacing: ".2em" }}>TELEGRAM</span>
      <span style={{ color: col, fontWeight: 700, letterSpacing: ".2em" }}>{label}</span>
    </div>
  );
}
function RiskBadge({ p }) {
  if (!p) return null;
  const c = RL[p.status]; const pulse = p.status === "amber" || p.status === "red";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.4vw" }}>
      <span style={{ color: C.muted, fontSize: "1.3vh", letterSpacing: ".2em" }}>S{p.setup}</span>
      <span className={pulse ? "tv-pulse" : ""} style={{
        padding: "0.3vh 0.8vw", borderRadius: 999,
        background: `${c}22`, border: `1px solid ${c}`, color: c,
        fontWeight: 700, letterSpacing: ".15em", boxShadow: `0 0 0.8vh ${c}aa`,
      }}>{p.status.toUpperCase()}</span>
    </div>
  );
}
function fmtCountdown(sec) {
  if (sec <= 0) return "";
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// =======================================================================
// Row 2 Left · Radar + Neural Map (persistent, rotating neural)
// =======================================================================
function TVRadar({ data }) {
  const radar = data?.radar || [];
  const [ts, setTs] = useState(0);
  const rafRef = useRef();
  useEffect(() => {
    let last = performance.now();
    const tick = (t) => {
      if (!document.hidden && t - last > 1000 / 24) { last = t; setTs((v) => v + 1); }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);
  const sweepAngle = (ts * 4) % 360;

  const positions = useMemo(() => {
    const splitmix32 = (seed) => {
      let x = 0;
      for (let i = 0; i < seed.length; i++) x = ((x << 5) - x + seed.charCodeAt(i)) | 0;
      x = (x ^ 0x9e3779b9) >>> 0;
      x = Math.imul(x ^ (x >>> 16), 0x85ebca6b);
      x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35);
      return ((x ^ (x >>> 16)) >>> 0) / 4294967296;
    };
    const GOLD = Math.PI * (3 - Math.sqrt(5));
    const stateRank = (s) => s === "in_trade" ? 0 : s === "idle" ? 1 : s === "etf" ? 2 : 3;
    const total = radar.length || 1;
    const ordered = radar.map((b, i) => ({ b, i, rank: stateRank(b.state), j: splitmix32(b.symbol) }))
                         .sort((a, b) => a.rank - b.rank || a.j - b.j);
    return ordered.map((row, k) => {
      const angle = (k * GOLD * (180 / Math.PI)) % 360;
      const base = 55 + Math.sqrt((k + 0.5) / total) * 170;
      const r = Math.max(45, Math.min(220, base + (row.j - 0.5) * 24));
      return { ...row.b, angle, r };
    });
  }, [radar]);

  return (
    <section style={panelStyle}>
      <PanelTitle>Radar · Target Search</PanelTitle>
      <div style={{ position: "relative", flex: 1, minHeight: 0, borderRadius: "0.4vh", overflow: "hidden" }}>
        <div className="ora-cosmos-bg" />
        <svg viewBox="-250 -250 500 500" preserveAspectRatio="xMidYMid meet"
             style={{ width: "100%", height: "100%", position: "relative", zIndex: 1 }}>
          <defs>
            <radialGradient id="tv-r-cyan" cx="35%" cy="30%"><stop offset="0%" stopColor="#aef1ff" /><stop offset="60%" stopColor="#3bd9ff" /><stop offset="100%" stopColor="#0c4a66" /></radialGradient>
            <radialGradient id="tv-r-green" cx="35%" cy="30%"><stop offset="0%" stopColor="#c7fde4" /><stop offset="60%" stopColor="#5bf0b3" /><stop offset="100%" stopColor="#0e4a37" /></radialGradient>
            <radialGradient id="tv-r-amber" cx="35%" cy="30%"><stop offset="0%" stopColor="#ffe3b2" /><stop offset="60%" stopColor="#ffb547" /><stop offset="100%" stopColor="#4a2d0e" /></radialGradient>
            <radialGradient id="tv-r-grey"  cx="35%" cy="30%"><stop offset="0%" stopColor="#d0e5ec" /><stop offset="60%" stopColor="#7fb8c9" /><stop offset="100%" stopColor="#1f3240" /></radialGradient>
          </defs>
          {[80, 140, 200, 230].map((r) => <circle key={r} cx="0" cy="0" r={r} fill="none" stroke={C.stroke} strokeWidth="1" />)}
          <line x1="-230" y1="0" x2="230" y2="0" stroke={C.stroke} strokeWidth="1" />
          <line x1="0" y1="-230" x2="0" y2="230" stroke={C.stroke} strokeWidth="1" />
          <g transform={`rotate(${sweepAngle})`}>
            <path d="M0,0 L230,0 A230,230 0 0,0 199.19,-115 Z" fill={C.cyan} opacity="0.14" />
          </g>
          {positions.map((b) => {
            const a = (b.angle * Math.PI) / 180;
            const x = Math.cos(a) * b.r, y = Math.sin(a) * b.r;
            const grad = b.state === "in_trade" ? "tv-r-green" : b.state === "blocked" ? "tv-r-amber" : b.state === "etf" ? "tv-r-grey" : "tv-r-cyan";
            const col  = b.state === "in_trade" ? C.green    : b.state === "blocked" ? C.amber    : b.state === "etf" ? "#7fb8c9" : C.cyan;
            const size = b.state === "in_trade" ? 11 : 7;
            return (
              <g key={b.symbol}>
                {b.state === "in_trade" && <circle cx={x} cy={y} r={size + 8} fill={col} opacity="0.26" className="tv-blink" />}
                <circle cx={x} cy={y} r={size} fill={`url(#${grad})`} className={b.state === "in_trade" ? "tv-blink" : undefined} />
                <ellipse cx={x - size * 0.35} cy={y - size * 0.4} rx={size * 0.35} ry={size * 0.22} fill="rgba(255,255,255,0.75)" />
                <text x={x} y={y - size - 6} textAnchor="middle" fill={C.text} fontSize="13" fontWeight="700"
                      style={{ paintOrder: "stroke", stroke: "rgba(5,14,30,.9)", strokeWidth: 2 }}>{b.symbol}</text>
              </g>
            );
          })}
          <circle cx="0" cy="0" r="7" fill={C.cyan} />
        </svg>
      </div>
    </section>
  );
}

function TVNeural({ data }) {
  // Rotating 3D cluster plot — slow spin, pauses when tab hidden.
  const [angle, setAngle] = useState(0);
  const rafRef = useRef();
  useEffect(() => {
    let last = performance.now();
    const tick = (t) => {
      if (!document.hidden && t - last > 1000 / 30) {
        const dt = (t - last) / 1000;
        last = t;
        setAngle((a) => a + dt * 0.35); // slow + readable
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const nodes = useMemo(() => {
    const wl = data?.watchlist || [];
    const KNOWN = {
      BT: new Set(["AAPL","NVDA","TSLA","NFLX","PLTR","AVGO"]),
      SM: new Set(["AMD","TSM","MRVL","AMAT","LRCX","SOXL","DELL"]),
      DF: new Set(["COST","LIN","COKE","QQQM"]),
    };
    const g = { BT: [], SM: [], DF: [], OT: [] };
    for (const w of wl) {
      const s = String(w.symbol || "").toUpperCase(); if (!s) continue;
      if (KNOWN.BT.has(s)) g.BT.push(w);
      else if (KNOWN.SM.has(s)) g.SM.push(w);
      else if (KNOWN.DF.has(s)) g.DF.push(w);
      else g.OT.push(w);
    }
    g.DF = g.DF.concat(g.OT); delete g.OT;
    const centres = { BT: [-1.0, 0.1, 0.1], SM: [1.0, 0.1, 0.3], DF: [0, -0.75, -0.3] };
    const out = [];
    for (const [k, arr] of Object.entries(g)) {
      if (!arr.length) continue;
      arr.forEach((w, i) => {
        const [cx, cy, cz] = centres[k];
        const a = (i / arr.length) * Math.PI * 2;
        out.push({
          sym: w.symbol,
          x: cx + Math.cos(a) * 0.33,
          y: cy + Math.sin(a) * 0.24,
          z: cz + Math.sin(a * 2) * 0.38,
          state: w.open_trade ? "in_trade" : w.is_etf ? "etf" : w.blocked_today_reason ? "blocked" : "idle",
        });
      });
    }
    return out;
  }, [data?.watchlist]);

  const project = (p) => {
    const cy = Math.cos(angle), sy = Math.sin(angle);
    const x1 = p.x * cy - p.z * sy;
    const z1 = p.x * sy + p.z * cy;
    const persp = 520 / (520 + z1 * 220);
    return { px: x1 * 160 * persp, py: p.y * 160 * persp, k: persp, z: z1 };
  };

  // Pre-project and sort back-to-front so closer nodes draw on top.
  const projected = useMemo(() => {
    return nodes.map((n) => ({ n, pr: project(n) })).sort((a, b) => b.pr.z - a.pr.z);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, angle]);

  return (
    <section style={panelStyle}>
      <PanelTitle>Neural Map</PanelTitle>
      <div style={{ flex: 1, minHeight: 0, position: "relative", borderRadius: "0.4vh", overflow: "hidden" }}>
        <div className="ora-cosmos-bg" />
        <svg viewBox="-280 -200 560 400" preserveAspectRatio="xMidYMid meet"
             style={{ width: "100%", height: "100%", position: "relative", zIndex: 1 }}>
          {/* intra-cluster links — low-opacity */}
          {projected.map(({ n, pr }, i) => projected.slice(i + 1).map(({ n: n2, pr: pr2 }, j) => {
            const dx = n.x - n2.x, dy = n.y - n2.y, dz = n.z - n2.z;
            if (dx*dx + dy*dy + dz*dz > 0.9) return null;
            return <line key={`l-${i}-${j}`} x1={pr.px} y1={pr.py} x2={pr2.px} y2={pr2.py}
                         stroke={C.cyan} strokeOpacity="0.14" strokeWidth="1" />;
          }))}
          {projected.map(({ n, pr }) => {
            const col = n.state === "in_trade" ? C.green : n.state === "blocked" ? C.amber : n.state === "etf" ? "#7fb8c9" : C.cyan;
            const r = (n.state === "in_trade" ? 10 : 7) * pr.k;
            return (
              <g key={n.sym}>
                <circle cx={pr.px} cy={pr.py} r={r} fill={col} opacity={0.4 + 0.5 * pr.k}
                        className={n.state === "in_trade" ? "tv-blink" : undefined} />
                <text x={pr.px} y={pr.py - r - 4} textAnchor="middle" fontSize={11 * pr.k + 2} fontWeight="700" fill={C.text}
                      style={{ paintOrder: "stroke", stroke: "rgba(5,14,30,.9)", strokeWidth: 2 }}>{n.sym}</text>
              </g>
            );
          })}
          <circle cx="0" cy="0" r="18" fill={C.cyan} opacity="0.4" />
          <text x="0" y="4" textAnchor="middle" fontSize="12" fontWeight="800" fill={C.text}>ORACLE</text>
        </svg>
      </div>
    </section>
  );
}

// =======================================================================
// Row 3 · Today's Signals  (PERMANENT)
// =======================================================================
function TVSignals({ data, prices }) {
  const sigs = data?.todays_signals || [];
  return (
    <section data-testid="tv-signals" style={panelStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: "1vw", marginBottom: "0.6vh" }}>
        <PanelTitle inline>Today's Signals · {sigs.length}</PanelTitle>
      </div>
      {sigs.length === 0 ? (
        <div style={{ color: C.muted, fontSize: "1.8vh", padding: "1vh 0" }}>
          No signals triggered yet today. Scanner is active during market hours.
        </div>
      ) : (
        <div style={{
          flex: 1, minHeight: 0, display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(14vw, 1fr))",
          gap: "0.8vh 0.8vw", overflow: "hidden",
        }}>
          {sigs.slice(0, 12).map((t, i) => (
            <SignalCard key={i} t={t} px={prices?.[String(t.symbol || "").toUpperCase()]} />
          ))}
        </div>
      )}
    </section>
  );
}

function SignalCard({ t, px }) {
  let timeNy = t.entry_time_ny || "", timeUk = t.entry_time_uk || "";
  if ((!timeNy || !timeUk) && t.entry_time) {
    try {
      const d = new Date(t.entry_time); d.setUTCMinutes(d.getUTCMinutes() + 30);
      if (!timeNy) timeNy = d.toLocaleTimeString("en-GB", { hour:"2-digit", minute:"2-digit", hour12:false, timeZone:"America/New_York" });
      if (!timeUk) timeUk = d.toLocaleTimeString("en-GB", { hour:"2-digit", minute:"2-digit", hour12:false, timeZone:"Europe/London" });
    } catch {}
  }
  const risk = (t.risk_dollars != null && t.risk_dollars !== 0) ? t.risk_dollars
    : (t.shares && t.entry != null && t.stop != null ? (t.entry - t.stop) * t.shares : null);
  return (
    <article style={{
      border: `1px solid ${C.stroke}`, borderRadius: "0.6vh",
      background: "rgba(5,14,30,.6)", padding: "0.6vh 0.7vw", display: "flex",
      flexDirection: "column", gap: "0.3vh", minHeight: 0,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: "1.9vh", fontWeight: 800 }}>{t.symbol}</span>
        <span style={{ fontSize: "1.1vh", color: C.cyan, border: `1px solid ${C.cyan}44`, padding: "0.1vh 0.4vw", borderRadius: 999, letterSpacing: ".2em" }}>S{t.setup}</span>
      </div>
      <div style={{ fontSize: "1.1vh", color: C.muted }}>{timeNy} NY · {timeUk} UK</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "0.3vw", fontSize: "1.2vh" }}>
        <TileBuy label="BUY" value={t.entry} color={C.cyan} />
        <TileBuy label="STOP" value={t.stop} color={C.red} />
        <TileBuy label="TGT"  value={t.target} color={C.green} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "1.1vh", color: C.muted }}>
        <span>{t.shares} sh</span>
        <span>risk ${risk != null ? risk.toFixed(0) : "—"}</span>
      </div>
      {(() => {
        if (!px || px.price == null) return null;
        const live = Number(px.price);
        const delta = (t.entry != null && t.entry > 0) ? (live / t.entry - 1) : null;
        const col = delta == null ? C.cyan : (delta >= 0 ? C.green : C.red);
        return (
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            fontSize: "1.1vh", paddingTop: "0.3vh",
            borderTop: `1px dashed ${C.stroke}`,
          }}>
            <span style={{ color: C.muted }}>
              LIVE <span style={{ color: col, fontWeight: 700 }}>${live.toFixed(2)}</span>
              {px.stale && <span style={{ marginLeft: 4, opacity: .5, fontSize: "0.9vh", letterSpacing: ".15em" }}>STALE</span>}
            </span>
            {delta != null && (
              <span style={{ color: col, fontWeight: 700 }}>
                {delta >= 0 ? "+" : ""}{(delta * 100).toFixed(2)}%
              </span>
            )}
          </div>
        );
      })()}
    </article>
  );
}
function TileBuy({ label, value, color }) {
  return (
    <div style={{ border: `1px solid ${color}44`, borderRadius: "0.4vh", padding: "0.2vh 0.3vw", textAlign: "center" }}>
      <div style={{ fontSize: "0.95vh", color: C.muted, letterSpacing: ".2em" }}>{label}</div>
      <div style={{ color, fontWeight: 700, fontSize: "1.4vh" }}>{value != null ? Number(value).toFixed(2) : "—"}</div>
    </div>
  );
}

// =======================================================================
// Row 4 · Carousel pages
// =======================================================================
function TVCarousel({ pageKey, enriched, totals, data, live }) {
  return (
    <section style={{ ...panelStyle, overflow: "hidden" }}>
      {pageKey === "open"    && <PageOpen enriched={enriched} totals={totals} />}
      {pageKey === "risk"    && <PageRisk live={live} />}
      {pageKey === "reactor" && <PageReactor data={data} />}
      {pageKey === "closed"  && <PageClosed data={data} />}
      {pageKey === "blocked" && <PageBlocked data={data} />}
    </section>
  );
}

function PageOpen({ enriched, totals }) {
  return (
    <>
      <div style={{ display: "flex", gap: "1.5vw", marginBottom: "0.6vh", alignItems: "center", flexWrap: "wrap" }}>
        <PanelTitle inline>Open Positions · {enriched.length}</PanelTitle>
        <SetupTotals label="S5" t={totals.s5} />
        <SetupTotals label="S6" t={totals.s6} />
      </div>
      {enriched.length === 0 ? (
        <div style={{ color: C.muted, fontSize: "1.8vh", padding: "1vh" }}>No open positions.</div>
      ) : (
        <div style={{
          flex: 1, minHeight: 0, overflow: "hidden",
          display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
          gap: "0.8vh 0.8vw", alignContent: "start",
        }}>
          {enriched.slice(0, 9).map((t) => (
            <OpenCard key={t._id || `${t.symbol}-${t.setup}`} t={t} />
          ))}
        </div>
      )}
    </>
  );
}

function OpenCard({ t }) {
  const hasPrice = t.last_price != null;
  const col = hasPrice ? (t.pnl_dollars >= 0 ? C.green : C.red) : C.muted;
  const maxHold = t.setup === 6 ? 15 : 5;
  return (
    <article style={{
      border: `1px solid ${C.stroke}`, borderRadius: "0.6vh",
      background: "rgba(5,14,30,.6)", padding: "0.6vh 0.8vw",
      display: "flex", flexDirection: "column", gap: "0.4vh", minHeight: 0,
    }}>
      {/* Header: symbol + setup chip + last price (right) */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4vw" }}>
          <span style={{ fontSize: "2vh", fontWeight: 800, letterSpacing: ".05em" }}>{t.symbol}</span>
          <span style={{
            fontSize: "1vh", color: C.cyan,
            border: `1px solid ${C.cyan}44`, padding: "0.1vh 0.4vw",
            borderRadius: 999, letterSpacing: ".2em",
          }}>S{t.setup}</span>
        </div>
        <div style={{ textAlign: "right", lineHeight: 1.1 }}>
          <div style={{ fontSize: "1.7vh", fontWeight: 700, color: hasPrice ? C.text : C.muted }}>
            {hasPrice ? t.last_price.toFixed(2) : "—"}
          </div>
          <div style={{ fontSize: "0.95vh", color: C.muted }}>
            {hasPrice ? (t.stale ? "stale" : "live") : "waiting"}
          </div>
        </div>
      </div>

      {/* Gradient stop → target bar */}
      <TVStopTargetBar entry={t.entry} stop={t.stop} target={t.target} pos={t.bar_pos} hasPrice={hasPrice} />
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.95vh", color: C.muted, marginTop: "-0.1vh" }}>
        <span>{Number(t.stop).toFixed(2)}</span>
        <span>{Number(t.target).toFixed(2)}</span>
      </div>

      {/* Entry / shares / days row */}
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "1.15vh", color: C.muted }}>
        <span>Buy <span style={{ color: C.text, fontWeight: 600 }}>{Number(t.entry).toFixed(2)}</span></span>
        <span>{t.shares} sh</span>
        <span>D {t.days_held ?? 0}/{maxHold}</span>
      </div>

      {/* P&L tiles — mirror the main page */}
      {hasPrice ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "0.3vw" }}>
          <PnlTile label="P&L $" value={signed(t.pnl_dollars)} color={col} />
          <PnlTile label="P&L %" value={`${t.pnl_pct >= 0 ? "+" : ""}${(t.pnl_pct * 100).toFixed(2)}%`} color={col} />
          <PnlTile label="P&L R" value={`${t.pnl_r >= 0 ? "+" : ""}${t.pnl_r.toFixed(2)}R`} color={col} />
        </div>
      ) : (
        <div style={{ fontSize: "1vh", color: C.muted, fontStyle: "italic" }}>Waiting for live price…</div>
      )}
    </article>
  );
}
function PnlTile({ label, value, color }) {
  return (
    <div style={{
      border: `1px solid ${color}44`, borderRadius: "0.4vh",
      padding: "0.2vh 0.3vw", textAlign: "center",
      background: "rgba(0,0,0,.25)",
    }}>
      <div style={{ fontSize: "0.9vh", color: C.muted, letterSpacing: ".2em" }}>{label}</div>
      <div style={{ color, fontWeight: 700, fontSize: "1.3vh" }}>{value}</div>
    </div>
  );
}
function SetupTotals({ label, t }) {
  if (!t?.count) return null;
  const col = t.pnl >= 0 ? C.green : C.red;
  return (
    <span style={{ display: "flex", alignItems: "center", gap: "0.4vw", fontSize: "1.4vh" }}>
      <span style={{ color: C.muted, letterSpacing: ".2em" }}>{label}</span>
      <span style={{ color: col }}>{signed(t.pnl)}</span>
      <span style={{ color: C.muted }}>· {t.count} open</span>
      <span style={{ color: C.muted }}>· {money(t.cost)}</span>
    </span>
  );
}
function TVStopTargetBar({ entry, stop, target, pos, hasPrice }) {
  const entryPos = (target === stop) ? 0.5 : Math.max(0, Math.min(1, (entry - stop) / (target - stop)));
  const safePos = (pos == null || Number.isNaN(pos)) ? entryPos : Math.max(0, Math.min(1, pos));
  return (
    <div style={{ position: "relative", width: "100%", height: "1.4vh" }}>
      <div style={{ position:"absolute", inset:0, borderRadius:999,
        background: "linear-gradient(90deg, rgba(255,92,122,.3), rgba(59,217,255,.14) 50%, rgba(69,240,166,.3))",
        border: `1px solid ${C.stroke}` }} />
      <div style={{ position:"absolute", top:0, bottom:0, left:`calc(${entryPos * 100}% - 1px)`, width:2, background:"rgba(207,234,255,.6)" }} />
      {hasPrice && (
        <div style={{
          position:"absolute", top:"50%", transform:"translate(-50%, -50%)",
          left:`${safePos * 100}%`, width:"1.2vh", height:"1.2vh", borderRadius:999,
          background: C.text, boxShadow:`0 0 0.6vh ${C.cyan}`, border:`1px solid ${C.cyan}`,
        }} />
      )}
    </div>
  );
}

function PageRisk({ live }) {
  const s5 = live?.setups?.find((p) => p.setup === 5);
  const s6 = live?.setups?.find((p) => p.setup === 6);
  return (
    <>
      <PanelTitle inline>Risk Lab · Live Paper</PanelTitle>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.8vw", marginTop: "0.8vh", flex: 1, minHeight: 0 }}>
        <RiskCard p={s5} />
        <RiskCard p={s6} />
      </div>
    </>
  );
}
function RiskCard({ p }) {
  if (!p) return <div style={{ color: C.muted, padding: "1vh" }}>Waiting…</div>;
  const m = p.metrics || {}; const c = RL[p.status]; const pulse = p.status === "amber" || p.status === "red";
  return (
    <article style={{ border: `1px solid ${c}66`, borderRadius: "0.6vh", background: "rgba(5,14,30,.5)",
      padding: "1.2vh 1vw", display: "grid", gridTemplateRows: "auto auto 1fr auto", gap: "0.8vh" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontWeight: 800, fontSize: "1.9vh", letterSpacing: ".2em" }}>SETUP {p.setup}</span>
        <span className={pulse ? "tv-pulse" : ""} style={{
          padding: "0.4vh 0.9vw", borderRadius: 999, color: c, background: `${c}22`, border: `1px solid ${c}`,
          fontWeight: 700, letterSpacing: ".2em", boxShadow: `0 0 0.8vh ${c}aa`, fontSize: "1.4vh",
        }}>● {p.status.toUpperCase()}</span>
      </div>
      <div style={{ fontSize: "1.3vh", color: C.muted }}>{p.meaning}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "0.6vw", fontSize: "1.5vh" }}>
        <TVStat label="EQUITY" value={money(m.current_equity)} />
        <TVStat label="PEAK"   value={money(m.peak_equity)} />
        <TVStat label="DROP"   value={pct(m.drawdown_pct)} color={m.drawdown_pct > 0 ? c : undefined} />
        <TVStat label="STREAK" value={`${m.losing_streak || 0}`} />
      </div>
      <div style={{ fontSize: "1.2vh", color: C.muted }}>
        Closed: <span style={{ color: C.text }}>{m.closed_trades || 0}</span> of ≈{m.expected_trades || 0} · max streak {m.max_losing_streak || 0}
      </div>
    </article>
  );
}
function TVStat({ label, value, color }) {
  return (
    <div>
      <div style={{ color: C.muted, fontSize: "1.1vh", letterSpacing: ".2em" }}>{label}</div>
      <div style={{ color: color || C.text, fontWeight: 700, fontSize: "1.6vh" }}>{value}</div>
    </div>
  );
}

function PageReactor({ data }) {
  const r = data?.reactor || {};
  const row = (key, label) => {
    const s = r[key] || {};
    const col = (s.daily_pnl_dollars || 0) >= 0 ? C.green : C.red;
    return (
      <article style={{ border: `1px solid ${C.stroke}`, borderRadius: "0.6vh",
        background: "rgba(5,14,30,.5)", padding: "1vh 1vw", display: "flex", flexDirection: "column", gap: "0.6vh" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 800, fontSize: "1.9vh", letterSpacing: ".2em" }}>{label}</span>
          <span style={{ color: C.muted, fontSize: "1.3vh" }}>{s.open_positions || 0} open</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "0.8vw", fontSize: "1.4vh" }}>
          <div>
            <div style={{ color: C.muted, fontSize: "1.1vh", letterSpacing: ".2em" }}>CAPITAL IN USE</div>
            <div style={{ color: C.text, fontWeight: 700, fontSize: "1.6vh" }}>{money(s.capital_in_use || 0)}</div>
            <div style={{ color: C.muted, fontSize: "1.1vh" }}>{(s.capital_in_use_x || 0).toFixed(2)}× of {money(r.account_size)}</div>
          </div>
          <div>
            <div style={{ color: C.muted, fontSize: "1.1vh", letterSpacing: ".2em" }}>RISK AT STAKE</div>
            <div style={{ color: C.text, fontWeight: 700, fontSize: "1.6vh" }}>{money(s.risk_at_stake || 0)}</div>
            <div style={{ color: C.muted, fontSize: "1.1vh" }}>shares × (buy − stop)</div>
          </div>
          <div>
            <div style={{ color: C.muted, fontSize: "1.1vh", letterSpacing: ".2em" }}>DAILY P/L</div>
            <div style={{ color: col, fontWeight: 700, fontSize: "1.6vh" }}>{signed(s.daily_pnl_dollars || 0)}</div>
            <div style={{ color: col, fontSize: "1.1vh" }}>{(s.daily_pnl_r || 0) >= 0 ? "+" : ""}{(s.daily_pnl_r || 0).toFixed(2)}R</div>
          </div>
        </div>
      </article>
    );
  };
  return (
    <>
      <PanelTitle inline>Reactor · Limits</PanelTitle>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.8vw", marginTop: "0.8vh" }}>
        {row("setup_5", "SETUP 5")}
        {row("setup_6", "SETUP 6")}
      </div>
      <div style={{ color: C.muted, fontSize: "1.2vh", marginTop: "1vh" }}>
        Totals · {r.open_positions || 0} open · risk {money(r.risk_in_use_dollars)} · daily {signed(r.daily_pnl_dollars || 0)} · period {(r.period_pnl_r || 0).toFixed(2)}R
      </div>
    </>
  );
}

function PageClosed({ data }) {
  const closed = (data?.closed_trades || []).slice(0, 10);
  return (
    <>
      <PanelTitle inline>Recent Closed · {(data?.closed_trades || []).length}</PanelTitle>
      {closed.length === 0 ? (
        <div style={{ color: C.muted, fontSize: "1.6vh", padding: "1vh" }}>No closed trades yet.</div>
      ) : (
        <table style={{ width: "100%", fontSize: "1.5vh", borderCollapse: "collapse", marginTop: "0.6vh" }}>
          <thead>
            <tr style={{ color: C.muted, fontSize: "1.2vh", letterSpacing: ".2em" }}>
              <th style={th()}>SYMBOL</th><th style={th()}>S</th>
              <th style={th()}>ENTERED</th><th style={th()}>EXITED</th>
              <th style={th("right")}>ENTRY</th><th style={th("right")}>EXIT</th>
              <th style={th("right")}>R</th><th style={th("right")}>$</th><th style={th()}>REASON</th>
            </tr>
          </thead>
          <tbody>
            {closed.map((t, i) => {
              const r = Number(t.r || 0); const col = r >= 0 ? C.green : C.red;
              return (
                <tr key={i} style={{ borderBottom: `1px solid rgba(59,217,255,.1)` }}>
                  <td style={td()}><span style={{ fontWeight: 700 }}>{t.symbol}</span></td>
                  <td style={td()}>S{t.setup}</td>
                  <td style={td()}>{t.entry_date}</td>
                  <td style={td()}>{t.exit_date}</td>
                  <td style={td("right")}>{Number(t.entry || 0).toFixed(2)}</td>
                  <td style={td("right")}>{Number(t.exit || 0).toFixed(2)}</td>
                  <td style={{ ...td("right"), color: col }}>{r >= 0 ? "+" : ""}{r.toFixed(2)}R</td>
                  <td style={{ ...td("right"), color: col }}>{signed(t.pnl_dollars)}</td>
                  <td style={td()}>{t.exit_reason}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}

function PageBlocked({ data }) {
  const blocked = (data?.watchlist || []).filter((w) => w.blocked_today_reason);
  return (
    <>
      <PanelTitle inline>Blocked Today · {blocked.length}</PanelTitle>
      {blocked.length === 0 ? (
        <div style={{ color: C.muted, fontSize: "1.6vh", padding: "1vh" }}>None blocked today.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(16vw, 1fr))", gap: "0.6vh 0.8vw", marginTop: "0.8vh" }}>
          {blocked.map((w) => (
            <div key={w.symbol} style={{ background: "rgba(5,14,30,.5)", border: `1px solid ${C.amber}44`,
              borderRadius: "0.5vh", padding: "0.6vh 0.8vw", display: "flex", gap: "0.6vw", fontSize: "1.5vh" }}>
              <span style={{ fontWeight: 700, color: C.amber, minWidth: "4vw" }}>{w.symbol}</span>
              <span style={{ color: C.text }}>{w.blocked_today_reason}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// =======================================================================
// Row 5 · Controls
// =======================================================================
function TVControls({ page, setPage, pinned, setPinned, pages, dim, onToggleDim, onExit, canInstall, isInstalled, onInstall }) {
  const prev = useCallback(() => { setPage((p) => (p - 1 + pages.length) % pages.length); setPinned(true); }, [pages.length, setPage, setPinned]);
  const next = useCallback(() => { setPage((p) => (p + 1) % pages.length); setPinned(true); }, [pages.length, setPage, setPinned]);
  const current = pages[page];
  return (
    <footer style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: "1vw", padding: "0 1vw",
      background: C.panel, border: `1px solid ${C.stroke}`, borderRadius: "0.6vh",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.6vw" }}>
        <TVBtn onClick={prev} label="◀" testid="tv-btn-prev" />
        <span style={{ fontSize: "1.4vh", color: C.muted, letterSpacing: ".2em", minWidth: "16vw", textAlign: "center" }}>
          {current.label.toUpperCase()} · <span style={{ color: C.text }}>{page + 1}/{pages.length}</span>
          {pinned && <span style={{ marginLeft: "0.6vw", color: C.amber }}>· PINNED</span>}
          {!pinned && <span style={{ marginLeft: "0.6vw", color: C.muted }}>· auto 20s</span>}
        </span>
        <TVBtn onClick={next} label="▶" testid="tv-btn-next" />
      </div>
      <div style={{ fontSize: "1.2vh", color: C.muted, letterSpacing: ".2em" }}>
        ← → PAGES · SPACE PIN · AUTO-REFRESH 60s
        {isInstalled && <span style={{ marginLeft: "0.6vw", color: C.green }}>· APP MODE</span>}
      </div>
      <div style={{ display: "flex", gap: "0.4vw" }}>
        {canInstall && (
          <TVBtn onClick={onInstall} label="INSTALL APP"
                 testid="tv-btn-install" active />
        )}
        <TVBtn onClick={() => setPinned((v) => !v)} label={pinned ? "UNPIN" : "PIN"} testid="tv-btn-pin" active={pinned} />
        <TVBtn onClick={onToggleDim} label={dim ? "LIT" : "DIM"} testid="tv-btn-dim" />
        <TVBtn onClick={onExit} label="EXIT" testid="tv-btn-exit" />
      </div>
    </footer>
  );
}
function TVBtn({ onClick, label, testid, active }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
            style={{
              padding: "0.4vh 0.9vw",
              background: active ? `${C.amber}22` : "transparent",
              color: active ? C.amber : C.cyan,
              border: `1px solid ${active ? C.amber : C.cyan}66`,
              borderRadius: "0.4vh", fontSize: "1.3vh", fontFamily: "inherit",
              letterSpacing: ".2em", cursor: "pointer", minWidth: "3vw",
            }}>{label}</button>
  );
}

// =======================================================================
// Install help dialog — shown when Chrome will not fire the auto install
// prompt (user already installed, Firefox, Safari, etc.). Gives the exact
// menu path for every major browser plus the Terminal one-liner.
// =======================================================================
function InstallHelpDialog({ onClose }) {
  const url = (typeof window !== "undefined") ? window.location.href : "";
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");
  const cmdMac =
`"/Applications/Google Chrome.app/Contents/MacOS/Google Chrome" \\
  --app=${url} --window-size=960,2160 --window-position=960,0`;
  const cmdWin =
`"C:\\Program Files\\Google\\Chrome\\Application\\chrome.exe" ^
  --app=${url} --window-size=960,1080 --window-position=960,0`;
  return (
    <div data-testid="tv-install-help"
         onClick={onClose}
         style={{
           position: "fixed", inset: 0, zIndex: 9999,
           background: "rgba(0,0,0,.72)",
           display: "flex", alignItems: "center", justifyContent: "center",
           padding: "4vh",
         }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{
             maxWidth: 720, width: "100%",
             background: "#0a1430",
             border: `1px solid ${C.stroke}`, borderRadius: 12,
             padding: "28px 32px", color: C.text,
             fontFamily: "inherit", lineHeight: 1.5,
             boxShadow: "0 20px 60px rgba(0,0,0,.6)",
           }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, letterSpacing: ".05em", margin: 0 }}>
            Install Oracle TV as an app
          </h2>
          <button onClick={onClose} data-testid="tv-install-help-close"
                  style={{ background: "transparent", color: C.muted, border: "none",
                           fontSize: 24, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        <p style={{ color: C.muted, fontSize: 14, marginTop: 0 }}>
          A chromeless window has no address bar or tabs and can be snapped to half the screen,
          exactly what you want for the TV layout.
        </p>

        <h3 style={{ fontSize: 14, color: C.cyan, letterSpacing: ".15em", marginTop: 20, marginBottom: 8 }}>
          OPTION A · CHROME / EDGE MENU
        </h3>
        <ol style={{ margin: "0 0 8px 18px", padding: 0, fontSize: 14 }}>
          <li>Click the three-dot menu <b>⋮</b> in the top-right of Chrome.</li>
          <li>Choose <b>Cast, save and share</b> → <b>Install page as app…</b>
              <span style={{ color: C.muted }}> (older Chrome: <b>More tools</b> → <b>Create shortcut…</b> and tick "Open as window").</span></li>
          <li>Name it "Oracle TV" → <b>Install</b>.</li>
          <li>The app opens in a chromeless window. Drag + resize it to half the screen.</li>
        </ol>
        <p style={{ fontSize: 13, color: C.muted }}>
          Edge: menu <b>⋯</b> → <b>Apps</b> → <b>Install this site as an app</b>.
        </p>

        <h3 style={{ fontSize: 14, color: C.cyan, letterSpacing: ".15em", marginTop: 20, marginBottom: 8 }}>
          OPTION B · TERMINAL ONE-LINER
        </h3>
        <p style={{ fontSize: 13, margin: "0 0 6px" }}>
          {isMac ? "macOS Terminal" : "Windows PowerShell / cmd"} — opens the TV view in a chromeless window pre-snapped to the right half of a 1920×1080/2160 screen. Adjust <code>--window-size</code> / <code>--window-position</code> for your resolution.
        </p>
        <pre data-testid="tv-install-help-cmd"
             style={{
               background: "#030818", border: `1px solid ${C.stroke}`,
               borderRadius: 6, padding: "10px 12px",
               fontSize: 12, color: C.cyan, overflowX: "auto",
               whiteSpace: "pre-wrap", wordBreak: "break-all",
             }}>{isMac ? cmdMac : cmdWin}</pre>
        <button
          type="button"
          data-testid="tv-install-help-copy"
          onClick={() => {
            try { navigator.clipboard.writeText(isMac ? cmdMac : cmdWin); } catch {}
          }}
          style={{
            marginTop: 8, padding: "6px 14px", background: "transparent",
            color: C.cyan, border: `1px solid ${C.cyan}66`,
            borderRadius: 6, cursor: "pointer", fontSize: 12,
            letterSpacing: ".15em",
          }}>
          COPY COMMAND
        </button>

        <h3 style={{ fontSize: 14, color: C.cyan, letterSpacing: ".15em", marginTop: 20, marginBottom: 8 }}>
          OPTION C · SAFARI (macOS 14+)
        </h3>
        <ol style={{ margin: "0 0 8px 18px", padding: 0, fontSize: 14 }}>
          <li>In Safari, go to <b>File</b> → <b>Add to Dock…</b>.</li>
          <li>Confirm the name <b>Oracle TV</b> → <b>Add</b>.</li>
          <li>Launch from the Dock — opens as a chromeless window.</li>
        </ol>

        <h3 style={{ fontSize: 14, color: C.amber, letterSpacing: ".15em", marginTop: 20, marginBottom: 8 }}>
          SNAP TO HALF SCREEN
        </h3>
        <ul style={{ margin: "0 0 12px 18px", padding: 0, fontSize: 14 }}>
          <li><b>macOS 15+:</b> drag the window to the right edge, hover until "Tile to Right" appears, release. Or use <b>Rectangle</b> / <b>Magnet</b> for a keyboard shortcut.</li>
          <li><b>Windows 11:</b> press <b>Win + →</b>.</li>
          <li><b>Linux (KDE / GNOME / Pop):</b> press <b>Super + →</b>.</li>
        </ul>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={onClose}
                  data-testid="tv-install-help-done"
                  style={{
                    padding: "8px 20px", background: C.cyan + "22",
                    color: C.cyan, border: `1px solid ${C.cyan}`,
                    borderRadius: 6, cursor: "pointer", fontSize: 13,
                    letterSpacing: ".15em", fontWeight: 700,
                  }}>GOT IT</button>
        </div>
      </div>
    </div>
  );
}


// =======================================================================
// Shared pieces
// =======================================================================
const panelStyle = {
  background: C.panel,
  border: `1px solid ${C.stroke}`,
  borderRadius: "0.6vh",
  padding: "1vh 1vw",
  display: "flex", flexDirection: "column",
  minHeight: 0, overflow: "hidden",
};
function PanelTitle({ children, inline }) {
  return (
    <div style={{
      color: C.cyan, fontWeight: 700, letterSpacing: ".3em",
      textTransform: "uppercase", fontSize: "1.3vh",
      marginBottom: inline ? 0 : "0.5vh",
      display: inline ? "inline-block" : "block",
      marginRight: inline ? "1vw" : 0,
    }}>{children}</div>
  );
}
function th(align) {
  return { textAlign: align || "left", padding: "0.4vh 0.5vw",
           borderBottom: `1px solid ${C.stroke}`, fontWeight: 400 };
}
function td(align) {
  return { textAlign: align || "left", padding: "0.4vh 0.5vw", color: C.text };
}

// =======================================================================
// Galaxy background + keyframes (self-contained, no external CSS deps).
// =======================================================================
if (typeof document !== "undefined" && !document.getElementById("tv-mode-css")) {
  const s = document.createElement("style");
  s.id = "tv-mode-css";
  s.textContent = `
    /* TV mode reuses .oracle-root nebula/stars/ora-cosmos-bg from the main
       stylesheet so the wall matches the regular display 1:1. We only add
       the small blink/pulse helpers here. */
    .tv-root { isolation: isolate; }
    @keyframes tv-blink  { 0%,100% { opacity:1; transform:scale(1) } 50% { opacity:.55; transform:scale(.9) } }
    .tv-blink { animation: tv-blink 1.2s ease-in-out infinite; transform-origin: center; transform-box: fill-box; }
    @keyframes tv-pulse  { 0%,100% { box-shadow: 0 0 0.6vh currentColor } 50% { box-shadow: 0 0 1.8vh currentColor } }
    .tv-pulse { animation: tv-pulse 1.6s ease-in-out infinite; }
    @media (prefers-reduced-motion: reduce) {
      .tv-blink, .tv-pulse { animation: none !important; }
    }
    /* Pause blink/pulse when tab hidden — the nebula/stars themselves are
       already paused via body.ora-tab-hidden by the shared stylesheet. */
    .tv-root.tv-hidden .tv-blink, .tv-root.tv-hidden .tv-pulse {
      animation-play-state: paused !important;
    }
  `;
  document.head.appendChild(s);

  // Pause on tab hidden
  const onVis = () => {
    const root = document.querySelector(".tv-root");
    if (!root) return;
    root.classList.toggle("tv-hidden", document.hidden);
  };
  document.addEventListener("visibilitychange", onVis);
}
